import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalMessageInboxSearchSettings } from "@/Communications/Models/Settings/ExternalMessageInboxSearchSettings";
import { UserSettings } from "@/Globals/Models/UserSettings";

interface IExternalMessageInboxSettingsContainerProps {
    onSave: (settings: ExternalMessageInboxSettingsContainerState) => void;
    onCancelClick: () => void;
    saveState: ExternalMessageInboxSettingsContainerState;
}

class ExternalMessageInboxSettingsContainerState {
    public receivedDateFrom: Date;
    public receivedDateTo: Date;
    public sharedWithSearchString: string;
    public isAllSharedWithChecked: boolean;
    public sharedWithList: EmployeeUIModel[];
    public selectedPageSizeOption: number;
    public isSaveSettingsChecked: boolean;

    public static createDefault(): ExternalMessageInboxSettingsContainerState {
        const settings = cacheStore.UserSettings?.ExternalMessageInboxSearchSettings;

        const sharedWithEmployees = cacheStore
            .GetCliniciansAsEmployees(CheckboxTarget.Unassigned, true)
            .filter(x => cacheStore.EmployeeSharingExternalCommunications.contains(x.ContactId));

        let state = new ExternalMessageInboxSettingsContainerState();
        state.selectedPageSizeOption = (settings && settings.SelectedPageSizeOption) || 50;
        state.receivedDateFrom = null;
        state.receivedDateTo = null;
        state.isSaveSettingsChecked = (settings && settings.IsAllSharedWithChecked) || false;
        state.sharedWithSearchString = "";
        state.sharedWithList = sharedWithEmployees.mutate(x => x.Checked = true, x => settings && settings.SharedWithContactFaceIds.indexOf(x.ContactFacetId) > -1) || [];

        return state;
    }
}

class ExternalMessageInboxSettingsContainer extends BaseReactComponent<IExternalMessageInboxSettingsContainerProps, ExternalMessageInboxSettingsContainerState> {
    constructor(props: IExternalMessageInboxSettingsContainerProps) {
        super(props);

        this.state = {
            selectedPageSizeOption: props.saveState.selectedPageSizeOption,
            receivedDateFrom: props.saveState.receivedDateFrom,
            receivedDateTo: props.saveState.receivedDateTo,
            sharedWithSearchString: "",
            isAllSharedWithChecked:
                props.saveState.sharedWithList?.length > 0 &&
                props.saveState.sharedWithList.all(p => p.Checked),
            sharedWithList: props.saveState.sharedWithList,
            isSaveSettingsChecked: props.saveState.isSaveSettingsChecked || false
        };
    }

    public componentDidMount(): void {
        this.addHandle(LocalOperations.Misc.MyleApp_Container_Click, () => this.onSave());
        this.setOverlay("ExternalMessageSettingsContainer", { transparent: true });
    }

    public render(): JSX.Element {
        const isReceivedDateValid = (this.state.receivedDateFrom && this.state.receivedDateTo) && (moment(this.state.receivedDateFrom) < moment(this.state.receivedDateTo));
        return (
            <div className="filter-component" style={{ width: "650px" }} onClick={e => e && e.stopPropagation()}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th className="first">
                                <label className="uppercase">{cacheStore.GetString("DateLabel")}</label>
                            </th>
                            <th>
                                <CheckboxControl
                                    id="xcomSharedWithAll"
                                    label={cacheStore.GetString("SharedWithLabel").toUpperCase()}
                                    checked={this.state.isAllSharedWithChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllSharedWithChecked = !s.isAllSharedWithChecked;
                                        s.sharedWithList.mutate(x => x.Checked = s.isAllSharedWithChecked);
                                    })}
                                />
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="first">
                                <div style={{ maxWidth: "230px" }}>
                                    <DateSelector
                                        id="xcomFromDate"
                                        date={this.state.receivedDateFrom}
                                        onChangeCallback={date => this.mutateState(s => s.receivedDateFrom = date && moment(date).startOf("day").toDate())}
                                        dateFormat="yyyy-MM-dd"
                                        disabled={false}
                                        toDayEnabled={true}
                                        valid={isReceivedDateValid && moment(this.state.receivedDateFrom).isValid()}
                                    />

                                    <DateSelector
                                        id="xcomToDate"
                                        date={this.state.receivedDateTo}
                                        onChangeCallback={date => this.mutateState(s => s.receivedDateTo = date && moment(date).endOf("day").toDate())}
                                        dateFormat="yyyy-MM-dd"
                                        disabled={false}
                                        toDayEnabled={true}
                                        valid={isReceivedDateValid && moment(this.state.receivedDateTo).isValid()}
                                    />
                                </div>
                            </td>
                            <td>
                                <TextboxControl
                                    debounceMs={400}
                                    value={this.state.sharedWithSearchString}
                                    onChange={(text: string) => this.mutateState(s => s.sharedWithSearchString = text)}
                                    className="form-control"
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ maxHeight: "400px", minHeight: "200px", overflow: "auto" }}>
                                    <CheckedListControl
                                        DataSource={SearchMenuHelper.FilterCliniciansByName(this.state.sharedWithSearchString, this.state.sharedWithList.toImmutable())}
                                        idModifier="xcomInboxSharedWit"
                                        onChange={(isChecked, id) => this.onSharedWithEmployeeChange(isChecked, id)}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    onClose={() => this.props.onCancelClick()}
                    onOk={() => this.onSave()}
                    onResultsPerPageChange={size => this.mutateState(s => s.selectedPageSizeOption = size)}
                    selectedPageSizeOption={this.state.selectedPageSizeOption}
                    isSaveSettingsChecked={this.state.isSaveSettingsChecked}
                    onSaveSettingsCheckboxChange={() => this.mutateState(s => s.isSaveSettingsChecked = !s.isSaveSettingsChecked)}
                />
            </div>
        );
    }

    private saveUserSettings(): void {
        const userSettings = new UserSettings();
        userSettings.ExternalMessageInboxSearchSettings = ExternalMessageInboxSearchSettings.fromModel(this.state);

        const requestData = {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        Client.Post(
            ServerOperations.Server.Myle_UpdateUserSettings,
            requestData,
            () => { }
        );

        cacheStore.UserSettings.MessageSearchSettings = userSettings.MessageSearchSettings;
    }

    private onSave(): void {
        if (this.state.isSaveSettingsChecked)
            this.saveUserSettings();

        this.props.onSave(this.state);
    }

    private onSharedWithEmployeeChange(isChecked: boolean, id: string): void {
        this.mutateState(s => {
            s.sharedWithList.mutate(x => x.Checked = isChecked, x => x.Id == id);
            s.isAllSharedWithChecked = s.sharedWithList.all(x => x.Checked);
        });
    }
}

export { ExternalMessageInboxSettingsContainer, ExternalMessageInboxSettingsContainerState };
